import React, { useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useResizeObserver from 'use-resize-observer';
import { Link } from 'wouter';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../concepts/resources/Icon';
import { Colors, Menu, remInPx } from '../../ui';
import { Badge } from '../../ui/Badge';
import { MenuFrame } from '../../ui/MenuFrame';
import { useResolve } from '../Container';
import { useOnTeamAddUserViaPopup } from '../actions/WorkspaceActions';
import { Popover } from '../canvas/ui/Popover';
import { Avatar } from '../common/Avatar';
import { UserModel, WorkspaceModel } from '../dashboard/Models';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { useLiveLocationInfo } from '../dashboard/members/MembersView';
import { useHangoutUser, useOnlineUsers } from '../hangouts/helpers/HangoutUserObserver';

const MemberInfo = React.memo<{ user: UserModel }>(({ user }) => {

    useObserver(user);
    const liveInfo = useLiveLocationInfo(user);

    return <Flexer padding="1 4" flex="column" center gap="1">
        <Avatar.ForUser user={user} size="5" />
        <Flexer flex="column" center gap=".25">
            <Title flush>{user.name}</Title>
            <Copy flush>{user.email}</Copy>
            <Copy flush>
                {!!liveInfo.label && liveInfo.href &&
                    <Link to={liveInfo.href} style={{ textDecoration: 'none' }}>
                        <Badge size="lg" color={Colors.positive}>{liveInfo.label}</Badge>
                    </Link>
                }
                {!!liveInfo.label && !liveInfo.href &&
                    <Badge size="lg" color={Colors.positive}>{liveInfo.label}</Badge>
                }
                {!liveInfo.label &&
                    <span>offline</span>
                }
            </Copy>
        </Flexer>
    </Flexer>;
});

const MemberItem = React.memo<{ user: UserModel }>(({ user }) => {

    const workspace = useObserver(useResolve(WorkspaceModel));
    const hu = useObserver(useHangoutUser(user.id));
    const status = (
        (hu?.location?.test(`workspace:${workspace.id}/board:*`) ? 'board' : null) ||
        (!!hu?.location ? 'online' : 'offline')
    );

    const [open, setOpen] = useState(false);

    return <Popover
        open={open}
        onOpenChange={setOpen}
        placement="bottom"
        inner={{ direction: 'down' }}
        content={<MemberInfo user={user} />}
    >
        <Avatar.ForUser clickable user={{ ...user.getState(), status }} size="2.5" />
    </Popover>;
});

const MemberMenu = React.memo<{ list: UserModel[] }>(({ list }) =>
    <MenuFrame>
        <Menu>
            {list.map(x =>
                <Menu.SubMenuItem key={x.id} placement="left-start" inner={{ direction: 'left', offset: '.5' }} content={<MemberInfo user={x} />}>
                    <Avatar.ForUser size="1.5" user={x} />
                    <Menu.ItemTextCropped>{x.name}</Menu.ItemTextCropped>
                </Menu.SubMenuItem>
            )}
        </Menu>
    </MenuFrame>);

const EveryoneElseButton = React.memo<{ list: UserModel[] }>(({ list }) => {

    const [open, setOpen] = useState(false);

    return <Popover
        open={open}
        onOpenChange={setOpen}
        placement="bottom-end"
        inner={{ offset: '.5', direction: 'down' }}
        content={<MemberMenu list={list} />}
    >
        <Avatar size="2.5" clickable color={Colors.primary}>
            +{list.length}
        </Avatar>
    </Popover>;
});

const MemberLikeButton = styled.div<{ color?: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 2.5rem;
    height: 2.5rem;
    border: solid .125rem rgba(0, 0, 0, .1);
    border-radius: 50%;
    color: rgba(0, 0, 0, .2);
    cursor: pointer;
    background: white;
    font-variant-numeric: tabular-nums;

    &:hover {
        border-color: rgba(0, 0, 0, .2);
        color: rgba(0, 0, 0, .3);
    }
    &:active {
        border-color: rgba(0, 0, 0, .3);
        color: rgba(0, 0, 0, .4);
    }

    ${p => p.color && css`
        background: ${p.color};
        color: ${Colors.contrastTextColorFor(p.color)} !important;
    `};
`;

const MemberStrip = () => {

    const workspace = useObserver(useResolve(WorkspaceModel));
    const { canTeamAddUser, onTeamAddUser } = useOnTeamAddUserViaPopup(workspace);
    const [count, setCount] = useState(0);

    const online = useOnlineUsers().slice(0, count);
    const lookup = useMemo(() => new Set(online.map(x => x.id)), [online]);

    const visible = useMemo(() => workspace.members.filter(x => lookup.has(x.id)), [workspace, count, lookup]);
    const hidden = useMemo(() => workspace.members.filter(x => !lookup.has(x.id)), [workspace, count, lookup]);

    const elmt = useRef<HTMLDivElement>(null);

    useResizeObserver({
        ref: elmt,
        onResize({ width }) {
            const next = Math.min(13, Math.floor((width - remInPx(4)) / remInPx(2.5)));
            if (next < workspace.members.length) {
                setCount(next - 1);
            }
            else {
                setCount(next);
            }
        }
    });

    const style = visible.length === 0 ? { opacity: 0, pointerEvents: 'none' as const } : {};

    return <Flexer ref={elmt} absolute inset flex="row" align="center" justify="flex-end" margin="0 0 0 1" style={style}>
        {visible.map(x =>
            <MemberItem key={x.id} user={x} />
        )}
        {!!hidden.length && <EveryoneElseButton list={hidden} />}
        <Flexer flex="row" align="center" justify="flex-end" width="4">
            {canTeamAddUser &&
                <MemberLikeButton onClick={onTeamAddUser}>
                    <Icon size="1.25" src="deqo://icons/hero/plus" />
                </MemberLikeButton>
            }
        </Flexer>
    </Flexer>;
};

export const TeamMemberStrip = () => {
    return <Flexer relative grow height="2.5">
        <MemberStrip />
    </Flexer>;
};

